import { useState, useCallback, useEffect, useRef } from 'react'
import { api } from '../../utils/api'
import {
  Download, AlertTriangle, CheckCircle2,
  FlaskConical, Info, ChevronDown, ChevronRight, Clock, AlertCircle, Activity
} from 'lucide-react'
import toast from 'react-hot-toast'

interface JobResult {
  id: number
  status: string  // pending | running | completed | completed_with_errors | failed
  progress_pct: number
  progress_msg: string | null
  dry_run: boolean
  racks_created: number
  racks_updated: number
  devices_created: number
  devices_updated: number
  ports_created: number
  cables_created: number
  cables_skipped: number
  errors: string | null
  netbox_url: string | null
  completed_at: string | null
  created_by: string | null
  client_ip: string | null
}

interface ImportHistory {
  id: number
  dc_code: string
  status: string
  progress_pct: number
  racks_created: number
  devices_created: number
  ports_created: number
  cables_created: number
  objects_total: number
  dry_run: boolean
  netbox_url: string | null
  created_at: string
  completed_at: string | null
  errors: string | null
  created_by: string | null
  client_ip: string | null
}

interface ApplyStepProps {
  url: string
  token: string
  dcCode: string
  siteId: number | null
  conflictStrategy: string
  selectedRackNbIds: number[] | null  // null = import all
  previewSummary: {
    racks: { create: number; update: number }
    devices: { create: number; update: number }
    ports: { create: number; update: number }
    cables: { create: number; update: number }
  }
}

function StatRow({ label, value, color = '' }: { label: string; value: number; color?: string }) {
  if (value === 0) return null
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-sm text-text-secondary">{label}</span>
      <span className={`text-sm font-semibold ${color || 'text-text-primary'}`}>{value}</span>
    </div>
  )
}

function ProgressBar({ pct, status }: { pct: number; status: string }) {
  const isError = status === 'failed'
  const isDone = status === 'completed' || status === 'completed_with_errors'
  const color = isError ? 'bg-red-500' : isDone ? 'bg-green-500' : 'bg-accent-primary'
  return (
    <div className="w-full bg-bg-input rounded-full h-2 overflow-hidden">
      <div
        className={`h-2 rounded-full transition-all duration-500 ${color} ${!isDone && !isError ? 'animate-pulse' : ''}`}
        style={{ width: `${Math.min(pct, 100)}%` }}
      />
    </div>
  )
}

export function NetBoxApplyStep({
  url, token, dcCode, siteId, conflictStrategy, selectedRackNbIds, previewSummary
}: ApplyStepProps) {
  const jobIdRef = useRef<number | null>(null)
  const [job, setJob] = useState<JobResult | null>(null)
  const [history, setHistory] = useState<ImportHistory[]>([])
  const [loading, setLoading] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const sseRef = useRef<EventSource | null>(null)

  const totalNew = (
    previewSummary.racks.create + previewSummary.devices.create +
    previewSummary.ports.create + previewSummary.cables.create
  )
  const totalUpdate = previewSummary.racks.update + previewSummary.devices.update + previewSummary.ports.update

  const onJobDone = useCallback((j: JobResult) => {
    setLoading(false)
    if (j.status === 'failed') {
      toast.error('Импорт завершился с ошибкой')
    } else if (j.status === 'completed_with_errors') {
      toast.success('Импорт завершён с предупреждениями')
    } else {
      toast.success(j.dry_run ? 'Dry Run завершён' : 'Импорт из NetBox выполнен!')
    }
    if (!j.dry_run && j.status !== 'failed') loadHistory()
  }, [])

  // SSE-based real-time progress (preferred)
  const startSSE = useCallback((id: number) => {
    if (sseRef.current) sseRef.current.close()
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null }

    const es = new EventSource(`/api/integrations/netbox/jobs/${id}/stream`)
    sseRef.current = es

    const handleData = (e: MessageEvent) => {
      try {
        const j: JobResult = JSON.parse(e.data)
        setJob(j)
      } catch { /* ignore parse errors */ }
    }

    es.addEventListener('progress', handleData)
    es.addEventListener('done', (e: MessageEvent) => {
      handleData(e)
      try {
        const j: JobResult = JSON.parse(e.data)
        onJobDone(j)
      } catch { /* ignore */ }
      es.close()
      sseRef.current = null
    })
    es.addEventListener('error', (_e) => {
      // SSE failed — fall back to polling
      es.close()
      sseRef.current = null
      startPollingFallback(id)
    })
  }, [onJobDone])

  // Polling fallback if SSE not available
  const startPollingFallback = useCallback((id: number) => {
    if (pollRef.current) clearInterval(pollRef.current)
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.get(`/api/integrations/netbox/jobs/${id}`)
        const j: JobResult = res.data
        setJob(j)
        const done = ['completed', 'completed_with_errors', 'failed'].includes(j.status)
        if (done) {
          clearInterval(pollRef.current!)
          pollRef.current = null
          onJobDone(j)
        }
      } catch { /* silent */ }
    }, 1500)
  }, [onJobDone])

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
      if (sseRef.current) sseRef.current.close()
    }
  }, [])

  const runImport = useCallback(async (dryRun: boolean) => {
    setLoading(true)
    setJob(null)
    jobIdRef.current = null
    setShowConfirm(false)
    try {
      const payload: Record<string, any> = {
        url: url || undefined,
        token: token || undefined,
        site_id: siteId,
        dc_code: dcCode,
        conflict_strategy: conflictStrategy,
        dry_run: dryRun,
        import_racks: true,
        import_devices: true,
        import_ports: true,
        import_cables: true,
        use_saved: true,
      }
      // Selective import: pass rack filter if not null
      if (selectedRackNbIds !== null && selectedRackNbIds.length > 0) {
        payload.selected_rack_nb_ids = selectedRackNbIds
      }
      const res = await api.post('/api/integrations/netbox/apply', payload)
      const id = res.data.job_id
      jobIdRef.current = id
      // Prefer SSE for real-time updates; falls back to polling automatically
      startSSE(id)
    } catch (e: any) {
      const msg = e.response?.data?.detail || e.message || 'Не удалось запустить импорт'
      setLoading(false)
      toast.error(msg)
    }
  }, [url, token, siteId, dcCode, conflictStrategy, selectedRackNbIds, startSSE])

  const loadHistory = useCallback(async () => {
    try {
      const res = await api.get(`/api/integrations/netbox/history?dc_code=${dcCode}&limit=10`)
      setHistory(res.data.history || [])
    } catch { /* silent */ }
  }, [dcCode])

  const isDone = job && ['completed', 'completed_with_errors', 'failed'].includes(job.status)

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-text-primary mb-1">Применить импорт</h2>
        <p className="text-sm text-text-secondary">
          Проверьте план и запустите импорт в ЦОД <strong>{dcCode}</strong>.
        </p>
      </div>

      {/* Plan summary */}
      <div className="rounded-lg border border-border bg-bg-panel p-4">
        <p className="text-xs text-text-muted uppercase tracking-wider mb-3">План импорта</p>
        <div className="divide-y divide-border/50">
          <StatRow label="Стоек создать"      value={previewSummary.racks.create}   color="text-green-400" />
          <StatRow label="Стоек обновить"     value={previewSummary.racks.update}   color="text-yellow-400" />
          <StatRow label="Устройств создать"  value={previewSummary.devices.create} color="text-green-400" />
          <StatRow label="Устройств обновить" value={previewSummary.devices.update} color="text-yellow-400" />
          <StatRow label="Портов создать"     value={previewSummary.ports.create}   color="text-green-400" />
          <StatRow label="Кабелей создать"    value={previewSummary.cables.create}  color="text-green-400" />
        </div>
        {totalNew === 0 && totalUpdate === 0 && (
          <p className="text-sm text-text-muted text-center py-2">Нет изменений для применения</p>
        )}
      </div>

      {/* Actions */}
      {!job && (
        <>
          <div className="flex gap-3">
            <button
              onClick={() => runImport(true)}
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 border border-border hover:border-accent-primary/50 bg-bg-panel text-text-secondary hover:text-text-primary rounded-lg text-sm font-medium transition-all disabled:opacity-50"
            >
              <FlaskConical className="w-4 h-4" />
              Dry Run (тест)
            </button>

            {!showConfirm ? (
              <button
                onClick={() => setShowConfirm(true)}
                disabled={loading || (totalNew === 0 && totalUpdate === 0)}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 bg-accent-primary hover:bg-accent-primary/90 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-all"
              >
                <Download className="w-4 h-4" /> Импортировать
              </button>
            ) : (
              <div className="flex-1 flex gap-2">
                <button
                  onClick={() => setShowConfirm(false)}
                  className="flex-1 py-2 px-3 border border-border rounded-lg text-sm text-text-secondary hover:text-text-primary transition-colors"
                >
                  Отмена
                </button>
                <button
                  onClick={() => runImport(false)}
                  className="flex-1 flex items-center justify-center gap-1 py-2 px-3 bg-red-600 hover:bg-red-500 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  <AlertCircle className="w-4 h-4" /> Подтвердить
                </button>
              </div>
            )}
          </div>

          {showConfirm && (
            <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/5 p-3 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-yellow-400 shrink-0 mt-0.5" />
              <p className="text-xs text-yellow-400">
                Импорт создаст <strong>{totalNew} новых</strong> и обновит <strong>{totalUpdate}</strong> объектов
                в ЦОД <strong>{dcCode}</strong>. Действие необратимо. Подтвердите для продолжения.
              </p>
            </div>
          )}

          {totalNew === 0 && totalUpdate === 0 && (
            <div className="rounded-lg border border-border bg-bg-panel/50 p-4 text-center">
              <CheckCircle2 className="w-8 h-8 text-green-400 mx-auto mb-2" />
              <p className="text-sm font-medium text-text-primary">Данные актуальны</p>
              <p className="text-xs text-text-muted mt-1">
                Все объекты из NetBox уже импортированы. Вернитесь на шаг 2 для другого сайта или ЦОД.
              </p>
            </div>
          )}
        </>
      )}

      {/* Live progress */}
      {(loading || job) && (
        <div className={`rounded-lg border p-4 space-y-3 ${
          job?.status === 'failed' ? 'border-red-500/30 bg-red-500/5' :
          isDone ? 'border-green-500/30 bg-green-500/5' :
          'border-accent-primary/30 bg-accent-primary/5'
        }`}>
          <div className="flex items-center gap-2">
            {loading && !isDone ? (
              <Activity className="w-4 h-4 text-accent-primary animate-pulse" />
            ) : job?.status === 'failed' ? (
              <AlertTriangle className="w-4 h-4 text-red-400" />
            ) : (
              <CheckCircle2 className="w-4 h-4 text-green-400" />
            )}
            <span className="text-sm font-medium text-text-primary">
              {!job ? 'Запуск...' :
               job.status === 'pending' ? 'Ожидание...' :
               job.status === 'failed' ? 'Ошибка импорта' :
               isDone ? (job.dry_run ? 'Dry Run завершён' : 'Импорт завершён') :
               'Импорт выполняется...'}
            </span>
            {job && (
              <span className="ml-auto text-xs text-text-muted">{job.progress_pct}%</span>
            )}
          </div>

          {job && <ProgressBar pct={job.progress_pct} status={job.status} />}
          {job?.progress_msg && (
            <p className="text-xs text-text-muted">{job.progress_msg}</p>
          )}

          {/* Result stats */}
          {isDone && job && job.status !== 'failed' && (
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm pt-2 border-t border-border/50">
              {job.racks_created > 0 && (
                <div className="flex justify-between">
                  <span className="text-text-secondary">Стоек создано</span>
                  <span className="text-green-400 font-medium">+{job.racks_created}</span>
                </div>
              )}
              {job.racks_updated > 0 && (
                <div className="flex justify-between">
                  <span className="text-text-secondary">Стоек обновлено</span>
                  <span className="text-yellow-400 font-medium">{job.racks_updated}</span>
                </div>
              )}
              {job.devices_created > 0 && (
                <div className="flex justify-between">
                  <span className="text-text-secondary">Устройств создано</span>
                  <span className="text-green-400 font-medium">+{job.devices_created}</span>
                </div>
              )}
              {job.devices_updated > 0 && (
                <div className="flex justify-between">
                  <span className="text-text-secondary">Устройств обновлено</span>
                  <span className="text-yellow-400 font-medium">{job.devices_updated}</span>
                </div>
              )}
              {job.ports_created > 0 && (
                <div className="flex justify-between">
                  <span className="text-text-secondary">Портов создано</span>
                  <span className="text-green-400 font-medium">+{job.ports_created}</span>
                </div>
              )}
              {job.cables_created > 0 && (
                <div className="flex justify-between">
                  <span className="text-text-secondary">Кабелей создано</span>
                  <span className="text-green-400 font-medium">+{job.cables_created}</span>
                </div>
              )}
              {job.cables_skipped > 0 && (
                <div className="flex justify-between">
                  <span className="text-text-secondary">Кабелей пропущено</span>
                  <span className="text-text-muted">{job.cables_skipped}</span>
                </div>
              )}
            </div>
          )}

          {job?.errors && (
            <div className="pt-2 border-t border-red-500/20">
              <p className="text-xs font-medium text-red-400 mb-1">Ошибки:</p>
              <p className="text-xs text-text-muted font-mono whitespace-pre-wrap line-clamp-4">
                {job.errors}
              </p>
            </div>
          )}

          {isDone && (
            <button
              onClick={() => { setJob(null); jobIdRef.current = null; setLoading(false) }}
              className="w-full py-1.5 text-xs text-text-muted hover:text-text-primary border border-border rounded-lg transition-colors"
            >
              Запустить снова
            </button>
          )}
        </div>
      )}

      <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-500/5 border border-blue-500/20">
        <Info className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
        <p className="text-xs text-text-muted">
          Импорт не удаляет существующие объекты. Стратегия конфликтов: <strong>{conflictStrategy}</strong>.
          Импорт выполняется асинхронно — страницу можно не держать открытой.
        </p>
      </div>

      {/* History */}
      <div>
        <button
          onClick={() => { setHistoryOpen(v => !v); if (!historyOpen) loadHistory() }}
          className="flex items-center gap-2 text-sm text-text-muted hover:text-text-primary transition-colors"
        >
          <Clock className="w-3.5 h-3.5" />
          История импортов
          {historyOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        </button>

        {historyOpen && history.length > 0 && (
          <div className="mt-2 rounded-lg border border-border overflow-hidden">
            {history.map(job => (
              <div key={job.id} className="flex items-center gap-3 px-4 py-2.5 border-b border-border last:border-0 text-xs">
                <span className={`w-2 h-2 rounded-full shrink-0 ${
                  job.status === 'completed' ? 'bg-green-400' :
                  job.status === 'completed_with_errors' ? 'bg-yellow-400' :
                  job.status === 'failed' ? 'bg-red-400' : 'bg-blue-400'
                }`} />
                <span className="text-text-muted font-mono">
                  {job.created_at ? new Date(job.created_at).toLocaleString('ru') : '—'}
                </span>
                <span className="text-text-primary">
                  +{job.racks_created} стоек, +{job.devices_created} устройств
                </span>
                {job.dry_run && <span className="text-text-muted italic">dry run</span>}
                <span className={`ml-auto font-medium ${
                  job.status === 'completed' ? 'text-green-400' :
                  job.status === 'failed' ? 'text-red-400' : 'text-yellow-400'
                }`}>{job.status}</span>
              </div>
            ))}
          </div>
        )}
        {historyOpen && history.length === 0 && (
          <p className="text-xs text-text-muted mt-2">Импортов ещё не было</p>
        )}
      </div>
    </div>
  )
}
